/** Mirrors server/src/lib/postfixRateDelay.ts (server is source of truth). */

const PER_IP_RATE = 2000;
const SMTP_COM_IPS = 2;
/** Empirical: 1 conn / 1s ≈ 1,800–1,900/hr ≈ 3600/(1+1). */
const SMTP_OVERHEAD_SEC = 1;
const NO_CAP_CONCURRENCY = 5;
const MAX_DELAY_SEC = 99;
const CONCURRENCY_MAX = 20;

export interface ClientPacingPreview {
  concurrency: number;
  delaySec: number;
  delayRaw: string;
  workersExpected: number;
  impliedRatePerHour: number;
  summary: string;
}

export function estimateActualRatePerHour(concurrency: number, delaySec: number): number {
  const c = Math.min(CONCURRENCY_MAX, Math.max(1, Math.round(concurrency) || 1));
  const d = Math.max(0, Math.round(Number(delaySec) || 0));
  if (d <= 0) return c * SMTP_COM_IPS * PER_IP_RATE;
  return Math.round(c * (3600 / (d + SMTP_OVERHEAD_SEC)));
}

export function previewPacingFromTarget(targetPerHour: number): ClientPacingPreview {
  const target = Math.max(0, Math.round(Number(targetPerHour) || 0));

  if (target <= 0) {
    const workers = NO_CAP_CONCURRENCY * SMTP_COM_IPS;
    return {
      concurrency: NO_CAP_CONCURRENCY,
      delaySec: 0,
      delayRaw: '0s',
      workersExpected: workers,
      impliedRatePerHour: estimateActualRatePerHour(NO_CAP_CONCURRENCY, 0),
      summary: `Will set smtp: ${NO_CAP_CONCURRENCY} conn / 0s (no cap, ~${workers} workers across ${SMTP_COM_IPS} IPs)`,
    };
  }

  const delayForOne = 3600 / target - SMTP_OVERHEAD_SEC;
  if (delayForOne >= 0.5) {
    const delaySec = Math.min(MAX_DELAY_SEC, Math.max(1, Math.round(delayForOne)));
    const implied = estimateActualRatePerHour(1, delaySec);
    return {
      concurrency: 1,
      delaySec,
      delayRaw: `${delaySec}s`,
      workersExpected: 1,
      impliedRatePerHour: implied,
      summary: `Will set smtp: 1 conn / ${delaySec}s (est. ~${implied.toLocaleString()}/hr)`,
    };
  }

  const perConnAt1s = 3600 / (1 + SMTP_OVERHEAD_SEC);
  const concAt1s = Math.round(target / perConnAt1s);
  if (concAt1s >= 2 && concAt1s <= CONCURRENCY_MAX) {
    const c = Math.min(CONCURRENCY_MAX, Math.max(1, concAt1s));
    const implied = estimateActualRatePerHour(c, 1);
    return {
      concurrency: c,
      delaySec: 1,
      delayRaw: '1s',
      workersExpected: c,
      impliedRatePerHour: implied,
      summary: `Will set smtp: ${c} conn / 1s (est. ~${implied.toLocaleString()}/hr)`,
    };
  }

  const floorPerConn = PER_IP_RATE * SMTP_COM_IPS;
  const postconfConcurrency = Math.min(
    CONCURRENCY_MAX,
    Math.max(1, Math.round(target / floorPerConn))
  );
  const workers = postconfConcurrency * SMTP_COM_IPS;
  const implied = estimateActualRatePerHour(postconfConcurrency, 0);
  return {
    concurrency: postconfConcurrency,
    delaySec: 0,
    delayRaw: '0s',
    workersExpected: workers,
    impliedRatePerHour: implied,
    summary: `Will set smtp: ${postconfConcurrency} conn/IP (${workers} workers) / 0s (est. ~${implied.toLocaleString()}/hr)`,
  };
}
